import { useState } from "react";

import type { Question } from "../api/types";

const FREE_TEXT_MAX = 5000;

type Props = {
  question: Question;
  onAnswer: (value: string) => void;
  disabled: boolean;
};

export function SurveyQuestion({ question, onAnswer, disabled }: Props) {
  if (question.type === "single_choice") {
    return (
      <div className="flex flex-1 flex-col">
        <h2 className="mb-12 text-[2rem] leading-[1.3]">
          {question.prompt}
        </h2>
        <div className="flex flex-col gap-4">
          {(question.options ?? []).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onAnswer(opt.value)}
              disabled={disabled}
              className="border border-[color:var(--color-wren-border)] bg-white p-6 text-left text-[1rem] leading-[1.5rem] text-[color:var(--color-wren-gray-700)] transition-colors duration-150 hover:border-[color:var(--color-wren-green)] hover:bg-[color:var(--color-wren-border-subtle)] active:border-[color:var(--color-wren-green-dark)] active:bg-[color:var(--color-wren-light-primary)] disabled:opacity-50"
              style={{
                borderRadius: "var(--radius-wren-card)",
                boxShadow: "var(--shadow-wren-button)",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <FreeTextQuestion question={question} onAnswer={onAnswer} disabled={disabled} />;
}

function FreeTextQuestion({ question, onAnswer, disabled }: Props) {
  const [value, setValue] = useState("");
  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= FREE_TEXT_MAX && !disabled;

  return (
    // Fill the remaining viewport height so the Continue button can pin to
    // the bottom regardless of textarea content length, matching the
    // assessment intro's bottom-anchored CTA pattern.
    <div className="flex flex-1 flex-col">
      <h2 className="mb-12 text-[2rem] leading-[1.3]">
        {question.prompt}
      </h2>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        rows={5}
        // Cap is enforced silently — the 5000-char ceiling is so far above
        // what anyone types in a survey free-response that surfacing a
        // "N / 5000" counter just adds anxious-looking metadata for no
        // benefit. The textarea hard-caps via maxLength; the backend
        // re-validates in survey.pmf.service._validate_answer.
        maxLength={FREE_TEXT_MAX}
        placeholder="Type your answer..."
        className="w-full border border-[color:var(--color-wren-border)] bg-white p-4 font-sans text-[1rem] leading-[1.5rem] text-[color:var(--color-wren-gray-700)] transition-colors duration-150 focus:border-[color:var(--color-wren-green)] focus:outline-none disabled:opacity-50"
        style={{
          borderRadius: "var(--radius-wren-card)",
          boxShadow: "var(--shadow-wren-button)",
        }}
      />
      <button
        type="button"
        onClick={() => onAnswer(trimmed)}
        disabled={!canSubmit}
        // mt-auto pushes the button to the bottom of the flex column so it
        // sits at the same vertical position as the intro's Begin button.
        className="mt-auto w-full bg-[color:var(--color-wren-green)] px-6 py-4 text-[1rem] font-semibold text-white transition-colors duration-150 hover:bg-[color:var(--color-wren-green-dark)] active:bg-[color:var(--color-wren-green-deep)] disabled:opacity-40"
        style={{
          borderRadius: "var(--radius-wren-button)",
          boxShadow: "var(--shadow-wren-button-inset)",
        }}
      >
        Continue
      </button>
    </div>
  );
}
